import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import {
  Bleed,
  Box,
  Button,
  Dialog,
  Divider,
  Skeleton,
  Stack,
  Text,
  TextLinkButton,
  useDialogControllers,
  useResponsiveValue
} from '@machobear/design-system'

import { useGetTerms } from '@/features/Agreements/use-get-terms'
import Policy from '@/features/GeneralInformation/policy'

import { useTermsDialogContext } from './context'

type Props = {
  visible: boolean
  onClose: () => void
  showCloseButton: boolean
  mismatchedMandatoryAgreements: string[]
  mismatchedOptionalAgreements: string[]
  actionRequiredTerms: string | null
  onTermsAccept: () => void
}

const renderContent = (data: string) => {
  const titleMatch = data?.match(/<h4>(.*?)<\/h4>/)
  const title = titleMatch ? titleMatch[1] : ''

  const contentMatch = data?.match(/<p>([^]*?)<\/p>/g)
  const content = contentMatch
    ? contentMatch.map((item: string) => item.replace(/<\/?p>/g, '')).join('\n')
    : ''

  const contentLines = content.split('\n')

  return (
    <>
      <Box paddingBottom="medium">
        <Text size="medium" weight="strong">
          {title}
        </Text>
      </Box>
      <Stack space="medium">
        {contentLines.map((line, index) => (
          <Text key={index} size="small">
            {line.trim()}
          </Text>
        ))}
      </Stack>
    </>
  )
}

const TermsDialog = ({
  visible,
  onClose,
  showCloseButton,
  mismatchedMandatoryAgreements,
  mismatchedOptionalAgreements,
  actionRequiredTerms,
  onTermsAccept
}: Props) => {
  const { t } = useTranslation()

  const { mismatched, latestAgreements } = useTermsDialogContext()

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const showPrivacyPolicy =
    !!mismatched?.privacy_policy || !!mismatched?.terms_conditions

  const [agreementId, setAgreementId] = useState('')

  const { terms, isLoading } = useGetTerms({
    id: agreementId,
    enabled: visible && !!agreementId
  })

  const {
    visible: visibleTerms,
    showModal,
    closeModal
  } = useDialogControllers(
    {
      privacyPolicyDialog: false,
      termsConditonsDialog: false
    },
    { stopPropagation: true }
  )

  const mandatoryAgreementsHaveBeenAccepted =
    mismatchedMandatoryAgreements.length === 0
  const optionalAgreementsHaveBeenAccepted =
    mismatchedOptionalAgreements.length === 0

  const dialogId = actionRequiredTerms || 'confirm_terms'
  const accecptId = `${dialogId}_click_accept`

  useEffect(() => {
    if (visible) {
      window.dataLayer.push({
        event: `${dialogId}_modal_opens`
      })
    }
  }, [visible, dialogId])

  const handleAccept = () => {
    if (actionRequiredTerms === 'trreb_agreements') {
      window.dataLayer.push({
        event: accecptId,
        termsType:
          dialogId === 'trreb_agreements'
            ? 'trrebterms_accepted'
            : `${dialogId}_accepted`
      })
    }
    onTermsAccept()
    setAgreementId('')
  }

  const title = isLoading ? (
    <Skeleton.Text lineCount={1} />
  ) : showPrivacyPolicy ? (
    <Trans
      i18nKey="b.agreement_modal.view.accept_and_continue.title"
      values={{
        terms: `${latestAgreements.privacy_policy.title} & ${latestAgreements.terms_conditions.title}`
      }}
      defaults="Our {{ terms }} have changed."
    />
  ) : (
    t('b.listings.terms_of_use.title', 'Terms of Use')
  )
  const handlePrivacyPolicyClick = () => {
    showModal('privacyPolicyDialog')()
    setAgreementId('privacy_policy')
  }
  const handleTermsConditionsClick = () => {
    showModal('termsConditonsDialog')()
    setAgreementId('terms_conditions')
  }
  const renderTermsContent = () => {
    if (showPrivacyPolicy) {
      const privacyPolicy = latestAgreements.privacy_policy.title
      const termsConditions = latestAgreements.terms_conditions.title

      return (
        <Box paddingTop="xxsmall">
          <Text>
            <Trans
              i18nKey="b.agreement_modal.view.accept_and_continue.description"
              values={{ privacyPolicy, termsConditions }}
            >
              We have updated our{' '}
              <TextLinkButton onClick={handlePrivacyPolicyClick}>
                {privacyPolicy}
              </TextLinkButton>{' '}
              and{' '}
              <TextLinkButton onClick={handleTermsConditionsClick}>
                {termsConditions}
              </TextLinkButton>
              . Please review these updates and let us know if you consent as
              part of your ongoing use of liv.rent.
            </Trans>
          </Text>
        </Box>
      )
    } else {
      return renderContent(terms?.content)
    }
  }

  useEffect(() => {
    if (mandatoryAgreementsHaveBeenAccepted && actionRequiredTerms)
      return setAgreementId(actionRequiredTerms)

    if (!mandatoryAgreementsHaveBeenAccepted)
      return setAgreementId(mismatchedMandatoryAgreements[0])

    if (!optionalAgreementsHaveBeenAccepted) {
      return setAgreementId(mismatchedOptionalAgreements[0])
    }

    return setAgreementId('')
  }, [
    mandatoryAgreementsHaveBeenAccepted,
    optionalAgreementsHaveBeenAccepted,
    actionRequiredTerms
  ])

  return (
    <>
      <Dialog
        open={visible}
        hideXButton={!showCloseButton}
        id={dialogId}
        title={title}
        description={
          <Text>
            {t(
              'b.listings.terms.accept_terms.text',
              'Please accept these terms & conditions'
            )}
          </Text>
        }
        onClose={onClose}
      >
        <Stack space="large">
          <Bleed horizontal={{ mobile: 'gutter', tablet: 'large' }}>
            <Divider />
          </Bleed>
          <Box
            overflow="auto"
            paddingBottom="large"
            paddingTop="xsmall"
            style={{ maxHeight: isMobile ? 'calc(100vh - 280px)' : '250px' }}
          >
            {isLoading ? <Skeleton.Text lineCount={6} /> : renderTermsContent()}
          </Box>
        </Stack>
        <Bleed horizontal={{ mobile: 'gutter', tablet: 'large' }}>
          <Divider />
        </Bleed>

        <Box
          width="full"
          display="flex"
          justifyContent="flexEnd"
          flexDirection={'row'}
          paddingTop="large"
        >
          {showCloseButton && (
            <Box>
              <Button
                variant="transparent"
                id={`${dialogId}_click_cancel`}
                onClick={onClose}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            </Box>
          )}
          <Box>
            <Button disabled={isLoading} id={accecptId} onClick={handleAccept}>
              {t('b.listing.terms.accept.button', 'I Accept')}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={
          visibleTerms.privacyPolicyDialog || visibleTerms.termsConditonsDialog
        }
        id={
          visibleTerms.privacyPolicyDialog
            ? 'privacy-policy-dialog'
            : 'terms-conditions-dialog'
        }
        title={
          visibleTerms.privacyPolicyDialog
            ? 'Privacy Policy'
            : 'Terms & Conditions'
        }
        onClose={closeModal(
          visibleTerms.privacyPolicyDialog
            ? 'privacyPolicyDialog'
            : 'termsConditonsDialog'
        )}
      >
        <Box
          overflow="auto"
          paddingBottom="large"
          style={{ maxHeight: isMobile ? 'calc(100vh - 280px)' : '500px' }}
        >
          {isLoading ? (
            <Skeleton.Text lineCount={6} />
          ) : (
            <Policy description={terms?.content} replaceLinks={false} />
          )}
        </Box>
      </Dialog>
    </>
  )
}

export default TermsDialog
